import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { 
  ArrowLeft, Plus, Trash2, Save, Package, Clock, Wrench, Calculator
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const QuoteEdit = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const isEdit = Boolean(quoteId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);
  
  const [formData, setFormData] = useState({
    client_id: '',
    valid_until: '',
    notes: '',
    items: []
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, materialsRes] = await Promise.all([
        api.get('/clients'),
        api.get('/materials')
      ]);
      setClients(clientsRes.data);
      setMaterials(materialsRes.data);

      if (isEdit) {
        const quoteRes = await api.get(`/quotes/${quoteId}`);
        const quote = quoteRes.data;
        setFormData({
          client_id: quote.client_id || '',
          valid_until: quote.valid_until || '',
          notes: quote.notes || '',
          items: quote.items || []
        });
      } else {
        // Set default valid_until to 30 days from now
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 30);
        setFormData(prev => ({
          ...prev,
          valid_until: defaultDate.toISOString().split('T')[0]
        }));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [api, isEdit, quoteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addItem = (type) => {
    const newItem = {
      id: `temp-${Date.now()}`,
      item_type: type,
      material_id: null,
      name: '',
      description: '',
      quantity: 1,
      unit: type === 'labor' ? 'hora' : 'unidad',
      unit_price: 0,
      tax_rate: 21,
      discount: 0
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // If selecting a material, auto-fill name and price
    if (field === 'material_id' && value) {
      const material = materials.find(m => m.id === value);
      if (material) {
        newItems[index].name = material.name;
        newItems[index].unit_price = material.unit_cost || 0;
        newItems[index].unit = material.unit || 'unidad';
      }
    }
    
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discountAmount = subtotal * ((item.discount || 0) / 100);
    const afterDiscount = subtotal - discountAmount;
    const tax = afterDiscount * ((item.tax_rate || 0) / 100);
    return afterDiscount + tax;
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    
    formData.items.forEach(item => {
      const itemSubtotal = (item.quantity || 0) * (item.unit_price || 0);
      const discountAmount = itemSubtotal * ((item.discount || 0) / 100);
      const afterDiscount = itemSubtotal - discountAmount;
      const itemTax = afterDiscount * ((item.tax_rate || 0) / 100);
      
      subtotal += afterDiscount;
      taxTotal += itemTax;
    });
    
    return {
      subtotal: subtotal.toFixed(2),
      taxTotal: taxTotal.toFixed(2),
      total: (subtotal + taxTotal).toFixed(2)
    };
  };

  const handleSubmit = async () => {
    if (!formData.client_id) {
      toast.error('Selecciona un cliente');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Añade al menos un item');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/quotes/${quoteId}`, {
          client_id: formData.client_id,
          items: formData.items,
          valid_until: formData.valid_until,
          notes: formData.notes
        });
        toast.success('Presupuesto actualizado');
      } else {
        await api.post('/quotes', {
          client_id: formData.client_id,
          items: formData.items,
          valid_until: formData.valid_until,
          notes: formData.notes
        });
        toast.success('Presupuesto creado');
      }
      navigate('/quotes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="quote-edit-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/quotes')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">
              {isEdit ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
            </h1>
          </div>
        </div>

        {/* Client & Details */}
        <Card>
          <CardHeader>
            <CardTitle>Datos del presupuesto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select 
                  value={formData.client_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Válido hasta</Label>
                <Input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notas o condiciones del presupuesto..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Add Item Buttons */}
        <div className="flex gap-3 flex-wrap">
          <Button variant="outline" onClick={() => addItem('material')}>
            <Package className="w-4 h-4 mr-2" />
            Añadir Material
          </Button>
          <Button variant="outline" onClick={() => addItem('labor')}>
            <Clock className="w-4 h-4 mr-2" />
            Añadir Mano de Obra
          </Button>
          <Button variant="outline" onClick={() => addItem('service')}>
            <Wrench className="w-4 h-4 mr-2" />
            Añadir Servicio
          </Button>
        </div>

        {/* Items List */}
        <Card>
          <CardHeader>
            <CardTitle>Líneas del presupuesto</CardTitle>
          </CardHeader>
          <CardContent>
            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Calculator className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Añade materiales, mano de obra o servicios</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={item.id} className="p-4 border rounded-lg bg-slate-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {item.item_type === 'material' && <Package className="w-4 h-4 text-blue-600" />}
                        {item.item_type === 'labor' && <Clock className="w-4 h-4 text-green-600" />}
                        {item.item_type === 'service' && <Wrench className="w-4 h-4 text-purple-600" />}
                        <span className="text-sm font-medium capitalize">
                          {item.item_type === 'material' ? 'Material' : item.item_type === 'labor' ? 'Mano de obra' : 'Servicio'}
                        </span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700"
                        onClick={() => removeItem(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                      {item.item_type === 'material' ? (
                        <div className="md:col-span-2">
                          <Label className="text-xs">Material</Label>
                          <Select
                            value={item.material_id || 'custom'}
                            onValueChange={(v) => updateItem(index, 'material_id', v === 'custom' ? null : v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="custom">Personalizado</SelectItem>
                              {materials.map(m => (
                                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="md:col-span-2">
                          <Label className="text-xs">Descripción</Label>
                          <Input
                            value={item.name}
                            onChange={(e) => updateItem(index, 'name', e.target.value)}
                            placeholder="Nombre del servicio"
                          />
                        </div>
                      )}
                      
                      {item.item_type === 'material' && !item.material_id && (
                        <div className="md:col-span-2">
                          <Label className="text-xs">Nombre</Label>
                          <Input
                            value={item.name}
                            onChange={(e) => updateItem(index, 'name', e.target.value)}
                            placeholder="Nombre del material"
                          />
                        </div>
                      )}
                      
                      <div>
                        <Label className="text-xs">Cantidad</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs">Precio/ud</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs">IVA %</Label>
                        <Select
                          value={String(item.tax_rate)}
                          onValueChange={(v) => updateItem(index, 'tax_rate', parseFloat(v))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0%</SelectItem>
                            <SelectItem value="4">4%</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="21">21%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-xs">Dto %</Label>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          value={item.discount}
                          onChange={(e) => updateItem(index, 'discount', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    
                    <div className="mt-3 text-right">
                      <span className="text-sm text-slate-500">Total línea: </span>
                      <span className="font-bold">{formatCurrency(calculateItemTotal(item))}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Totals */}
        {formData.items.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-end gap-2">
                <div className="flex justify-between w-full max-w-xs">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between w-full max-w-xs">
                  <span className="text-slate-600">IVA:</span>
                  <span className="font-medium">{formatCurrency(totals.taxTotal)}</span>
                </div>
                <div className="flex justify-between w-full max-w-xs pt-2 border-t">
                  <span className="text-lg font-bold">Total:</span>
                  <span className="text-lg font-bold text-sky-600">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/quotes')}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="bg-sky-600 hover:bg-sky-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar presupuesto'}
          </Button>
        </div>
      </div>
    </OwnerLayout>
  );
};

export default QuoteEdit;
